import type Phaser from 'phaser';

/**
 * **O fundo da arena e as estruturas de impulso** (gate V28H.K).
 *
 * ## Porque existe
 *
 * A arena passou a 24 000 x 15 000 unidades - 25x a área - e o contrato do
 * catálogo (gate V28H.B) pediu, no mesmo parágrafo, um **background próprio** e
 * **melhores efeitos visuais nas estruturas de impulso**. Uma caixa quase preta
 * cinco vezes mais larga do que o ecrã não diz a um jogador se ele está a andar:
 * sem referência nenhuma no chão, atravessar a arena parece estar parado.
 *
 * ## O que ele NÃO é
 *
 * Não é regra. Nada aqui decide dano, colisão, empurrão, alvo ou duração - as
 * posições e as direções vêm de `PVP_BOOSTERS`, a MESMA constante com que a sala
 * empurra, portanto o desenho não pode discordar do que acontece. A animação é
 * decoração local: ela lê o relógio da cena e nunca o tique da sala, e duas
 * abas com fases diferentes vêem o mesmo jogo.
 *
 * ## O orçamento, e porque ele não depende do tamanho da arena
 *
 * O céu são **dois** `TileSprite` presos à câmara (`scrollFactor` 0) com a
 * posição do padrão derivada do scroll - portanto o número de objectos é dois
 * quer a arena tenha 4 800 ou 24 000 de largura, e a paralaxe sai de graça. Uma
 * nuvem de estrelas espalhada pelo mundo precisaria de dezenas de milhares de
 * objectos para a mesma densidade, e é exactamente o que o `CLAUDE.md` proíbe
 * quando diz que a caça usa um conjunto fixo e reciclado.
 *
 * As texturas são **procedurais e criadas uma vez** por `Phaser.Game`: nenhum
 * ficheiro novo, nenhuma pasta de arte, e nada alocado por quadro.
 */

/** As chaves das texturas geradas. Um prefixo, para não colidir com arte real. */
const TEXTURE = {
  starsFar: 'pvp-vfx-stars-far',
  starsNear: 'pvp-vfx-stars-near',
  dust: 'pvp-vfx-dust',
  chevron: 'pvp-vfx-chevron',
  glow: 'pvp-vfx-glow',
} as const;

/** O lado de cada mosaico, em píxeis. Potência de dois: um `TileSprite` repete-o. */
const TILE = 256;

/**
 * A paralaxe de cada camada.
 *
 * `0` seria um céu pintado no ecrã, que não se mexe e não diz nada; `1` seria
 * chão, e o chão já existe. Os dois números abaixo são pequenos de propósito:
 * o que se quer é que o fundo **derive** enquanto a nave atravessa, e não que
 * corra com ela.
 *
 * Provisórios, como todo o equilíbrio visual deste gate.
 */
const PARALLAX_FAR = 0.06;
const PARALLAX_NEAR = 0.16;

/** Quantos píxeis por segundo os galões percorrem dentro de um impulsionador. */
const CHEVRON_FLOW_PX_PER_SECOND = 110;

/**
 * Uma sequência determinista, para as estrelas.
 *
 * `Math.random()` daria um céu diferente em cada aba e em cada recarga, o que é
 * inofensivo para a regra e mau para uma prova: um teste que quisesse afirmar
 * "a textura tem estrelas" não teria como. Isto é decoração, portanto o gerador
 * é local e não o da simulação - mas é fixo, e o céu é sempre o mesmo céu.
 */
function mulberry(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b_79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296;
  };
}

/**
 * Desenha um mosaico de estrelas e devolve-o como textura.
 *
 * Uma só textura por camada, repetida pelo `TileSprite`. As estrelas são pontos
 * de um a dois píxeis com alpha variável: a variedade vem do brilho e não da
 * cor, porque um céu multicolorido rouba contraste às naves e aos projéteis -
 * que são a única coisa nesta arena que o jogador tem mesmo de ler.
 */
function makeStarTexture(
  scene: Phaser.Scene,
  key: string,
  options: { readonly count: number; readonly seed: number; readonly maxAlpha: number },
): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const texture = scene.textures.createCanvas(key, TILE, TILE);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, TILE, TILE);
  const random = mulberry(options.seed);
  for (let index = 0; index < options.count; index += 1) {
    const x = Math.floor(random() * TILE);
    const y = Math.floor(random() * TILE);
    const alpha = 0.15 + random() * options.maxAlpha;
    const big = random() > 0.86;
    context.fillStyle = `rgba(255,255,255,${alpha.toFixed(3)})`;
    context.fillRect(x, y, big ? 2 : 1, big ? 2 : 1);
  }
  texture.refresh();
}

/**
 * O véu de poeira que assenta SOBRE o chão da arena.
 *
 * Distingue-se do céu por andar com o mundo (`scrollFactor` 1): é ele que diz,
 * quando a nave passa, que o chão é um sítio e não uma cor. Muito ténue de
 * propósito - o suficiente para haver textura, nunca o suficiente para competir
 * com um projétil.
 */
function makeDustTexture(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const texture = scene.textures.createCanvas(key, TILE, TILE);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, TILE, TILE);
  const random = mulberry(0x51_a3);
  for (let index = 0; index < 90; index += 1) {
    const x = random() * TILE;
    const y = random() * TILE;
    const radius = 6 + random() * 26;
    const gradient = context.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, 'rgba(64,102,150,0.055)');
    gradient.addColorStop(1, 'rgba(64,102,150,0)');
    context.fillStyle = gradient;
    context.beginPath();
    context.arc(x, y, radius, 0, Math.PI * 2);
    context.fill();
  }
  texture.refresh();
}

/**
 * Um galão, apontado para `+x`, num mosaico que se repete ao longo do impulso.
 *
 * A forma é o SINAL: um jogador tem de ver, sem legenda, para onde a estrutura
 * o vai atirar. O mosaico repete-se e a sua posição avança com o tempo, o que
 * dá o escorrer sem uma única partícula e sem alocar nada por quadro.
 */
function makeChevronTexture(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 64;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  context.strokeStyle = 'rgba(47,208,138,0.85)';
  context.lineWidth = 5;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  for (const offset of [-2, 30]) {
    context.beginPath();
    context.moveTo(offset + 6, 14);
    context.lineTo(offset + 26, size / 2);
    context.lineTo(offset + 6, size - 14);
    context.stroke();
  }
  texture.refresh();
}

/**
 * Um disco de luz, quase branco no meio e transparente na borda.
 *
 * Desenhado em `ADD` na saída de cada impulsionador: é o "para ali" que se lê
 * pelo canto do olho. Um só sprite por estrutura - a nota de gosto contra vários
 * halos sobrepostos no mesmo ponto é literal.
 */
function makeGlowTexture(scene: Phaser.Scene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const size = 128;
  const texture = scene.textures.createCanvas(key, size, size);
  const context = texture?.getContext();
  if (!texture || !context) {
    return;
  }
  context.clearRect(0, 0, size, size);
  const middle = size / 2;
  const gradient = context.createRadialGradient(middle, middle, 0, middle, middle, middle);
  gradient.addColorStop(0, 'rgba(214,255,236,0.95)');
  gradient.addColorStop(0.35, 'rgba(47,208,138,0.45)');
  gradient.addColorStop(1, 'rgba(47,208,138,0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, size, size);
  texture.refresh();
}

/** Cria as cinco texturas, uma vez por `Phaser.Game`. */
export function ensurePvpArenaTextures(scene: Phaser.Scene): void {
  makeStarTexture(scene, TEXTURE.starsFar, { count: 150, seed: 0x1f_2e, maxAlpha: 0.35 });
  makeStarTexture(scene, TEXTURE.starsNear, { count: 46, seed: 0x7c_11, maxAlpha: 0.7 });
  makeDustTexture(scene, TEXTURE.dust);
  makeChevronTexture(scene, TEXTURE.chevron);
  makeGlowTexture(scene, TEXTURE.glow);
}

/** Um impulsionador desenhado: as peças que ele possui, e o seu escorrer. */
interface BoosterVisual {
  readonly flow: Phaser.GameObjects.TileSprite;
  readonly glow: Phaser.GameObjects.Image;
  /** `+1` ou `-1` por eixo, para o padrão escorrer para onde a estrutura empurra. */
  readonly flowX: number;
  readonly flowY: number;
}

/**
 * O fundo e as estruturas, com um dono e um descarte verificável.
 *
 * Tudo o que esta classe cria está numa das suas listas, e `destroy()` esvazia
 * todas. Nada é criado fora de `build`, e `update` não aloca.
 */
export class PvpArenaVfx {
  private readonly scene: Phaser.Scene;
  /**
   * , entregue por quem chama.
   *
   * Um número e não um `import` do motor, e é deliberado: importar `phaser`
   * como VALOR arranca o registo de renderers inteiro, que num ambiente sem
   * canvas rebenta antes de a primeira prova correr. A cena já tem o motor
   * carregado e passa a constante; este ficheiro fica mensurável sem ele.
   */
  private readonly blendAdd: number;
  private far: Phaser.GameObjects.TileSprite | null = null;
  private near: Phaser.GameObjects.TileSprite | null = null;
  private dust: Phaser.GameObjects.TileSprite | null = null;
  private readonly boosters: BoosterVisual[] = [];
  /** Milissegundos acumulados, só para o escorrer. Nunca um tique de regra. */
  private elapsedMs = 0;

  constructor(scene: Phaser.Scene, blendAdd: number) {
    this.scene = scene;
    this.blendAdd = blendAdd;
  }

  /**
   * Constrói o céu e a poeira.
   *
   * O céu tem o tamanho do ECRÃ e não o da arena, e é isso que o torna barato:
   * ele está preso à câmara, e o que se mexe é a posição do padrão dentro dele.
   */
  buildBackground(
    viewportWidth: number,
    viewportHeight: number,
    arenaWidth: number,
    arenaHeight: number,
    depth: number,
  ): void {
    ensurePvpArenaTextures(this.scene);
    /*
     * Com folga: a câmara roda o zoom, e um céu do tamanho exacto do viewport
     * deixaria ver o seu próprio bordo ao afastar. Três vezes cobre todo o
     * intervalo de zoom que esta arena permite.
     */
    const width = viewportWidth * 3;
    const height = viewportHeight * 3;
    this.far = this.scene.add
      .tileSprite(0, 0, width, height, TEXTURE.starsFar)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(depth)
      .setAlpha(0.85);
    this.near = this.scene.add
      .tileSprite(0, 0, width, height, TEXTURE.starsNear)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(depth)
      .setAlpha(0.95);
    /*
     * A poeira anda COM o mundo, e é do tamanho da arena: ela é chão, e é o que
     * dá a um jogador a sensação de percorrer distância. Um `TileSprite` do
     * tamanho da arena continua a ser UM objecto - a repetição é do GPU.
     */
    this.dust = this.scene.add
      .tileSprite(0, 0, arenaWidth, arenaHeight, TEXTURE.dust)
      .setOrigin(0, 0)
      .setDepth(depth + 1);
  }

  /**
   * Constrói uma estrutura de impulso: base, galões a escorrer e luz na saída.
   *
   * Três camadas, na ordem que a direcção de arte pede - **núcleo** (a chapa e a
   * moldura, que dizem onde a estrutura está), **energia** (os galões, que dizem
   * para onde ela atira) e **resíduo** (o brilho na saída, o acabamento).
   *
   * As coordenadas e a direcção chegam já em unidades de mundo, derivadas da
   * mesma constante que a sala usa para empurrar.
   */
  buildBooster(
    box: { readonly x: number; readonly y: number; readonly w: number; readonly h: number },
    push: { readonly x: number; readonly y: number },
    depth: number,
  ): Phaser.GameObjects.GameObject[] {
    ensurePvpArenaTextures(this.scene);
    const centreX = box.x + box.w / 2;
    const centreY = box.y + box.h / 2;
    const heading = Math.atan2(push.y, push.x);

    const plate = this.scene.add
      .rectangle(centreX, centreY, box.w, box.h, 0x0d2a20, 0.5)
      .setStrokeStyle(1, 0x2fd08a, 0.55)
      .setDepth(depth);

    /*
     * Os galões vivem num `TileSprite` do tamanho da caixa, RODADO para a
     * direcção do empurrão. A rotação é do objecto e não da textura, portanto
     * há um mosaico só para as quatro estruturas.
     *
     * As dimensões trocam com a rotação: uma estrutura que empurra na vertical
     * é a mesma faixa, vista de lado.
     */
    const alongX = Math.abs(push.x) >= Math.abs(push.y);
    const flow = this.scene.add
      .tileSprite(centreX, centreY, alongX ? box.w : box.h, alongX ? box.h : box.w, TEXTURE.chevron)
      .setRotation(heading)
      .setDepth(depth + 1)
      .setAlpha(0.75)
      .setBlendMode(this.blendAdd);

    /*
     * E a luz na SAÍDA - a borda por onde a nave é cuspida - e não no meio: o
     * que ela marca é o destino, que é a informação que falta a um jogador que
     * entra na faixa pela primeira vez.
     */
    const exitX = centreX + Math.sign(push.x) * (box.w / 2);
    const exitY = centreY + Math.sign(push.y) * (box.h / 2);
    const glow = this.scene.add
      .image(exitX, exitY, TEXTURE.glow)
      .setDisplaySize(box.h * 1.1, box.h * 1.1)
      .setDepth(depth + 2)
      .setAlpha(0.5)
      .setBlendMode(this.blendAdd);

    this.boosters.push({ flow, glow, flowX: Math.sign(push.x), flowY: Math.sign(push.y) });
    return [plate, flow, glow];
  }

  /**
   * Um quadro: a paralaxe do céu e o escorrer dos galões.
   *
   * Sem alocação, sem `Math.random`, sem tween e sem timer. A fase sai do tempo
   * decorrido, portanto uma aba que esteve escondida retoma onde o relógio da
   * cena está e não acumula um salto.
   */
  update(deltaMs: number, scrollX: number, scrollY: number): void {
    this.elapsedMs += deltaMs;
    if (this.far) {
      this.far.tilePositionX = scrollX * PARALLAX_FAR;
      this.far.tilePositionY = scrollY * PARALLAX_FAR;
    }
    if (this.near) {
      this.near.tilePositionX = scrollX * PARALLAX_NEAR;
      this.near.tilePositionY = scrollY * PARALLAX_NEAR;
    }
    const travel = (this.elapsedMs / 1_000) * CHEVRON_FLOW_PX_PER_SECOND;
    for (const booster of this.boosters) {
      /*
       * Só `x`: o mosaico já está rodado para a direcção do empurrão, portanto
       * o eixo local `x` É essa direcção. Empurrar em `y` aqui faria os galões
       * escorrer de lado dentro da própria faixa.
       */
      booster.flow.tilePositionX = travel;
      /*
       * E a luz respira, entre metade e o dobro do seu alpha de repouso. Uma
       * pulsação lenta: rápida seria flicker, que a direcção de arte proíbe.
       */
      booster.glow.setAlpha(0.38 + 0.18 * Math.sin(this.elapsedMs / 420));
    }
  }

  /** O céu tem de acompanhar uma janela que mudou de tamanho. */
  resize(viewportWidth: number, viewportHeight: number): void {
    for (const sky of [this.far, this.near]) {
      sky?.setSize(viewportWidth * 3, viewportHeight * 3);
    }
  }

  /**
   * Larga tudo.
   *
   * As texturas NÃO são destruídas: elas pertencem ao `Phaser.Game` e são
   * partilhadas por todos os duelos que a aba abrir. Destruí-las obrigaria a
   * redesenhá-las a cada partida, que é exactamente o trabalho por quadro que
   * este ficheiro existe para não fazer.
   */
  destroy(): void {
    this.far?.destroy();
    this.far = null;
    this.near?.destroy();
    this.near = null;
    this.dust?.destroy();
    this.dust = null;
    /*
     * Os objectos dos impulsionadores são devolvidos por `buildBooster` e a cena
     * destrói-os com o resto da arena; aqui larga-se apenas a referência, para
     * um duelo seguinte não animar sprites de um duelo que acabou.
     */
    this.boosters.length = 0;
    this.elapsedMs = 0;
  }
}
